/**
 * Top-level page switcher that renders the current redux-first-router route.
 *
 * Looks up the active route type in the routes map (src/redact-app/pages/routes.ts),
 * lazy-loads that route's code-split component inside Suspense/RouteErrorScreen, shows a
 * loading screen until app boot finishes, and renders nothing when requiresAuth is set
 * but the user is not authenticated. For Redact, when the org-level folder view flag is
 * enabled it overrides the home/ingest routes to render CaseMediaPage instead of the
 * legacy MainPage. Suspense is keyed by route type to remount on navigation.
 *
 * It also owns Redact's single shared aiWARE app bar, rendered above the routed subtree for
 * routes whose entry sets `showsAppBar`. The pages each used to render their own, so every
 * navigation unmounted the bar and mounted a fresh SDK widget — a visible flash of a header
 * whose contents had not changed (VE-26729).
 *
 * The React.lazy wrappers are created ONCE per component importer and cached at module
 * scope. Calling lazy() per render (or per route change) produces a new component type
 * whose payload starts pending, so React suspends and flashes the full-page
 * RouteLoadingScreen on every navigation even though webpack already holds the chunk — and
 * a new type mid-session unmounts and remounts the whole page tree (VE-26729). The cache is
 * keyed by the importer rather than the route type so routes sharing a page component share
 * one resolved wrapper.
 */
import { lazy, Suspense, ComponentType } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { modules } from '@veritone/glc-redux';
import { isString } from 'lodash';

import NotFound from '@common-pages/NotFound';
import RouteErrorScreen from '@common-components/RouteErrorScreen';
import RouteLoadingScreen from '@common-components/RouteLoadingScreen';
import {
  ROUTE_HOME,
  ROUTE_INGEST,
  ROUTE_INGEST_GOVQA,
  selectRoutesMap,
  selectRouteType,
} from '@common-modules/routing';
import { bootDidFinish } from '@common-modules/app';
import { selectEnableFolderView } from '@common/user-onboarding/selectors/organization';
import { isRedact } from '@common-pages/App/helper';
import BatchOperationUnloadGuard from '@redact-components/BatchOperationUnloadGuard';
import AppBar from '@redact-components/AppBar';

const {
  user: { userIsAuthenticated },
} = modules;

// Importer -> its lazy wrapper. Keyed by the `component` function rather than by route
// type so that routes sharing a module share one wrapper: Home, Ingest and Ingest/GovQA
// all render MainPage, and a per-route wrapper would leave the other two unresolved and
// suspend — flashing the loading screen on a navigation between them. routes.ts hoists a
// single importer constant per shared module to make those references identical.
const lazyPageComponents = new Map<() => Promise<any>, ComponentType<any>>();

const getLazyPageComponent = (
  importComponent: () => Promise<any>
): ComponentType<any> => {
  const cached = lazyPageComponents.get(importComponent);
  if (cached) {
    return cached;
  }
  const created = lazy(importComponent);
  lazyPageComponents.set(importComponent, created);
  return created;
};

const CaseMediaPage = lazy(() => import(`@redact-pages/CaseMediaPage`));

const combinedSelectors = createSelector(
  selectRoutesMap,
  selectRouteType,
  userIsAuthenticated,
  bootDidFinish,
  (
    routesMap,
    routeType,
    isAuthed,
    isBooted,
    currentRoute:
      | ReturnType<typeof selectRoutesMap>['']
      | undefined = routesMap[routeType]
  ) => ({
    isPermittedToRenderCurrentPage:
      (!isString(currentRoute) && !currentRoute?.requiresAuth) || isAuthed,
    PageComponent:
      isString(currentRoute) || !currentRoute?.component
        ? NotFound
        : getLazyPageComponent(currentRoute.component),
    isLoading: !isBooted,
    routeType,
    showsAppBar: !isString(currentRoute) && !!currentRoute?.showsAppBar,
  })
);

const RootRouter = ({ appName }: { appName?: string }) => {
  const {
    routeType,
    PageComponent,
    isLoading,
    isPermittedToRenderCurrentPage,
    showsAppBar,
  } = useSelector(combinedSelectors);

  const isEnableFolderView = useSelector(selectEnableFolderView);
  let RenderPageComponent = PageComponent;
  const allowedRoutes: string[] = [
    ROUTE_HOME.type,
    ROUTE_INGEST.type,
    ROUTE_INGEST_GOVQA.type,
  ];
  if (
    isRedact(appName) &&
    isEnableFolderView &&
    allowedRoutes.includes(routeType)
  ) {
    RenderPageComponent = CaseMediaPage;
  }

  if (isLoading) {
    return <RouteLoadingScreen />;
  } else if (isPermittedToRenderCurrentPage) {
    return (
      <>
        {/* Outside the keyed Suspense on purpose: a batch file operation outlives the
            page that started it, so its unload guard has to outlive that page too. (It
            also sits outside RouteErrorScreen, but nothing rides on that: RouteErrorScreen
            is a fallback screen, not a class error boundary — it cannot catch a page's
            render throw.) */}
        {isRedact(appName) && <BatchOperationUnloadGuard />}
        {/* Also outside the keyed Suspense, and for the same reason it is here at all: the
            header is identical on every route that shows it, so it must not be unmounted
            and remounted by a navigation. Kept in a fixed position in this tree so React
            preserves the instance whenever both routes set `showsAppBar`. */}
        {isRedact(appName) && showsAppBar && <AppBar />}
        <RouteErrorScreen>
          <Suspense key={routeType} fallback={<RouteLoadingScreen />}>
            <RenderPageComponent />
          </Suspense>
        </RouteErrorScreen>
      </>
    );
  } else {
    return <></>;
  }
};

export default RootRouter;
